import { Link } from 'react-router-dom';

export default function HopeInDarknessPost() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-2xl font-bold text-purple-600">
              Kadın Hareketi
            </Link>
            <Link
              to="/blog"
              className="text-gray-600 hover:text-purple-600 transition-colors"
            >
              ← Back to Blog
            </Link>
          </div>
        </div>
      </nav>

      {/* Article */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <header className="mb-12">
          <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
            <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full">
              Empowerment
            </span>
            <span>January 2, 2025</span>
            <span>12 min read</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Hope in the Darkness: Why We Must Keep Fighting Even When It's Difficult
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Every day brings news that breaks our hearts. But hope is not naive optimism—it's radical resistance. Here's why we must hold onto it, even when the world feels heavy.
          </p>
        </header>

        {/* Featured Image */}
        <div className="mb-12 rounded-2xl overflow-hidden shadow-xl">
          <img
            src="https://public.readdy.ai/ai/img_res/63d07636daa937ec07772add57f7d66d.jpg"
            alt="Women holding candles in solidarity"
            className="w-full h-96 object-cover object-top"
          />
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          {/* Introduction */}
          <div className="bg-purple-50 border-l-4 border-purple-600 p-6 rounded-r-lg mb-8">
            <p className="text-lg text-gray-800 italic mb-0">
              "Hope is not the belief that everything will be okay. Hope is the belief that what we do matters, even when everything is not okay."
            </p>
          </div>

          <p className="text-gray-700 leading-relaxed mb-6">
            I write this on a day when the news is devastating. Another woman killed by her partner. Another country rolling back reproductive rights. Another war targeting women and children. Another day when it feels like the world is moving backward, not forward.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            And yet, I write about hope.
          </p>

          <p className="text-gray-700 leading-relaxed mb-8">
            Not because I'm naive. Not because I don't see the darkness. But because hope is not a luxury—it's a necessity. It's not weakness—it's resistance. It's not denial—it's defiance.
          </p>

          {/* Section 1 */}
          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            The Weight We Carry
          </h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            If you're reading this, you probably feel it too. The heaviness. The exhaustion. The grief that sits in your chest like a stone.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>You wake up and check the news:</strong>
          </p>

          <ul className="space-y-3 mb-6">
            <li className="text-gray-700">Another femicide in Turkey. Another woman's name added to the list we can't stop counting.</li>
            <li className="text-gray-700">Women in Afghanistan banned from speaking in public. Their voices literally stolen.</li>
            <li className="text-gray-700">Abortion rights stripped away in country after country. Women's bodies treated as property of the state.</li>
            <li className="text-gray-700">Trans women murdered at alarming rates. Their existence criminalized, their lives disposable.</li>
            <li className="text-gray-700">Women and children bombed in Gaza, Ukraine, Sudan. War always targets the most vulnerable.</li>
            <li className="text-gray-700">Climate crisis disproportionately affecting women in the Global South. Their homes, their futures, disappearing.</li>
          </ul>

          <p className="text-gray-700 leading-relaxed mb-8">
            And that's just today. Tomorrow will bring more. And the day after. And the day after that.
          </p>

          <div className="bg-gray-50 p-6 rounded-lg mb-8">
            <p className="text-gray-800 font-semibold mb-3">How do we keep going?</p>
            <p className="text-gray-700 mb-0">
              How do we hold onto hope when the world seems determined to crush it? When every step forward feels like it's met with ten steps back? When the systems we're fighting are so massive, so entrenched, so powerful?
            </p>
          </div>

          {/* Section 2 */}
          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            What Hope Is Not
          </h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Let me be clear: Hope is not toxic positivity. It's not pretending everything is fine. It's not "good vibes only" while the world burns.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Hope is not:</strong>
          </p>

          <div className="space-y-4 mb-8">
            <div className="border-l-4 border-red-400 pl-4">
              <p className="text-gray-700 font-semibold mb-1">Denial</p>
              <p className="text-gray-600 text-sm">Ignoring the reality of suffering and injustice</p>
            </div>
            <div className="border-l-4 border-red-400 pl-4">
              <p className="text-gray-700 font-semibold mb-1">Passivity</p>
              <p className="text-gray-600 text-sm">Waiting for someone else to fix things</p>
            </div>
            <div className="border-l-4 border-red-400 pl-4">
              <p className="text-gray-700 font-semibold mb-1">Naivety</p>
              <p className="text-gray-600 text-sm">Believing change will come easily or quickly</p>
            </div>
            <div className="border-l-4 border-red-400 pl-4">
              <p className="text-gray-700 font-semibold mb-1">Privilege</p>
              <p className="text-gray-600 text-sm">Only available to those who aren't directly affected</p>
            </div>
            <div className="border-l-4 border-red-400 pl-4">
              <p className="text-gray-700 font-semibold mb-1">Certainty</p>
              <p className="text-gray-600 text-sm">Knowing everything will work out</p>
            </div>
          </div>

          <p className="text-gray-700 leading-relaxed mb-8">
            Hope is not the belief that everything will be okay. Because sometimes, things are not okay. Sometimes, they're terrible. Sometimes, we lose. Sometimes, people die. Sometimes, rights are taken away. Sometimes, the bad guys win.
          </p>

          {/* Section 3 */}
          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            What Hope Actually Is
          </h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Hope is the belief that what we do matters.</strong>
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Even when we can't see the results. Even when the change is slow. Even when we won't live to see the world we're fighting for.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Hope is:
          </p>

          <div className="space-y-4 mb-8">
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">🔥 Active Resistance</p>
              <p className="text-gray-700 text-sm">Choosing to fight even when the odds are against us</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">💪 Radical Courage</p>
              <p className="text-gray-700 text-sm">Showing up every day despite fear and exhaustion</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">🤝 Collective Action</p>
              <p className="text-gray-700 text-sm">Believing that together, we are powerful</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">🌱 Long-Term Vision</p>
              <p className="text-gray-700 text-sm">Planting seeds we may never see bloom</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">❤️ Love in Action</p>
              <p className="text-gray-700 text-sm">Caring for each other and the world despite everything</p>
            </div>
          </div>

          <div className="bg-purple-100 border-l-4 border-purple-600 p-6 rounded-r-lg mb-8">
            <p className="text-lg text-gray-800 font-semibold mb-2">
              Hope is not optimism. It's commitment.
            </p>
            <p className="text-gray-700 mb-0">
              It's the decision to keep going even when you don't know if you'll win. It's the choice to care even when caring hurts. It's the refusal to let despair have the final word.
            </p>
          </div>

          {/* Section 4 */}
          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            Why We Must Hold Onto Hope
          </h2>

          <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">
            1. Because Despair Is What They Want
          </h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            The systems of oppression—patriarchy, capitalism, white supremacy, colonialism—they want us to give up. They want us exhausted. They want us hopeless. They want us to believe nothing will ever change.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Because when we give up, they win without a fight.</strong>
          </p>

          <p className="text-gray-700 leading-relaxed mb-8">
            Our hope is an act of rebellion. Our refusal to despair is resistance. Every time we choose to keep fighting, we deny them their victory.
          </p>

          <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">
            2. Because History Shows Us Change Is Possible
          </h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            Think about what women have achieved in just the last 100 years:
          </p>

          <ul className="space-y-3 mb-6">
            <li className="text-gray-700">We couldn't vote. Now we can (in most places).</li>
            <li className="text-gray-700">We couldn't own property. Now we can.</li>
            <li className="text-gray-700">We couldn't have bank accounts. Now we can.</li>
            <li className="text-gray-700">We couldn't work most jobs. Now we can.</li>
            <li className="text-gray-700">Marital rape was legal. Now it's a crime (in many places).</li>
            <li className="text-gray-700">Domestic violence was private. Now it's prosecuted.</li>
            <li className="text-gray-700">Sexual harassment was accepted. Now it's challenged.</li>
          </ul>

          <p className="text-gray-700 leading-relaxed mb-6">
            None of this happened because powerful people decided to be nice. It happened because women fought. Women organized. Women refused to accept the status quo. Women held onto hope even when it seemed impossible.
          </p>

          <div className="bg-gray-50 p-6 rounded-lg mb-8">
            <p className="text-gray-800 font-semibold mb-3">The suffragettes didn't know if they'd win.</p>
            <p className="text-gray-700 mb-3">The women who fought for reproductive rights didn't know if they'd succeed.</p>
            <p className="text-gray-700 mb-3">The women who created domestic violence shelters didn't know if anyone would care.</p>
            <p className="text-gray-700 mb-0">But they fought anyway. And we benefit from their hope today.</p>
          </div>

          <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">
            3. Because We Owe It to Those Who Come After Us
          </h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            We stand on the shoulders of women who fought battles they never won. Women who were arrested, beaten, killed for demanding rights they never got to enjoy.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            They held onto hope for us. Now we hold onto hope for the women who come after us.
          </p>

          <p className="text-gray-700 leading-relaxed mb-8">
            Maybe we won't see gender equality in our lifetimes. Maybe we won't see the end of violence against women. Maybe we won't see true justice. But our daughters might. Our granddaughters might. The girls being born today might.
          </p>

          <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">
            4. Because Small Acts Matter
          </h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            You don't have to change the world to make a difference. You don't have to be a hero. You don't have to do everything.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Every small act of resistance matters:</strong>
          </p>

          <ul className="space-y-3 mb-8">
            <li className="text-gray-700">The woman who leaves her abuser—she's changing the world</li>
            <li className="text-gray-700">The teacher who challenges sexist textbooks—she's changing the world</li>
            <li className="text-gray-700">The mother who raises her son to respect women—she's changing the world</li>
            <li className="text-gray-700">The friend who believes a survivor—she's changing the world</li>
            <li className="text-gray-700">The woman who refuses to smile on command—she's changing the world</li>
            <li className="text-gray-700">The girl who speaks up in class—she's changing the world</li>
            <li className="text-gray-700">You, reading this, refusing to give up—you're changing the world</li>
          </ul>

          <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">
            5. Because We Need Each Other
          </h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            Hope is not something we hold alone. It's something we create together.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            When you're exhausted, I'll carry the hope for both of us. When I'm despairing, you'll remind me why we fight. When we're both struggling, we'll lean on the women who came before us and the women fighting beside us.
          </p>

          <p className="text-gray-700 leading-relaxed mb-8">
            <strong>This is what solidarity means.</strong> Not that we're always strong. But that we take turns being strong for each other.
          </p>

          {/* Section 5 */}
          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            How to Hold Onto Hope When It's Hard
          </h2>

          <div className="space-y-6 mb-8">
            <div className="bg-white border border-purple-200 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">1. Limit Your News Consumption</h4>
              <p className="text-gray-700 mb-3">
                You need to stay informed, but you don't need to doomscroll. Set boundaries. Check the news once or twice a day, then step away.
              </p>
              <p className="text-gray-700 text-sm italic">
                You can't pour from an empty cup. Protect your mental health.
              </p>
            </div>

            <div className="bg-white border border-purple-200 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">2. Focus on What You Can Control</h4>
              <p className="text-gray-700 mb-3">
                You can't fix everything. But you can do something. Pick one issue. One organization. One action. Start there.
              </p>
              <p className="text-gray-700 text-sm">
                <strong>Examples:</strong> Volunteer at a women's shelter. Donate to reproductive rights organizations. Mentor a young woman. Support women-owned businesses. Vote. Protest. Write. Speak up.
              </p>
            </div>

            <div className="bg-white border border-purple-200 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">3. Celebrate Small Wins</h4>
              <p className="text-gray-700 mb-3">
                Don't wait for the revolution to celebrate. Celebrate every victory, no matter how small.
              </p>
              <p className="text-gray-700 text-sm">
                A woman got a restraining order. A girl stood up to her bully. A law passed protecting trans rights. A company implemented equal pay. These matter.
              </p>
            </div>

            <div className="bg-white border border-purple-200 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">4. Connect With Your Community</h4>
              <p className="text-gray-700 mb-3">
                Find your people. Join organizations. Attend protests. Go to support groups. Build relationships with women who understand.
              </p>
              <p className="text-gray-700 text-sm italic">
                Isolation breeds despair. Community breeds hope.
              </p>
            </div>

            <div className="bg-white border border-purple-200 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">5. Rest Is Resistance</h4>
              <p className="text-gray-700 mb-3">
                You don't have to be productive every moment. You don't have to be fighting 24/7. Rest. Sleep. Play. Laugh. Love. Live.
              </p>
              <p className="text-gray-700 text-sm">
                <strong>Remember:</strong> You're not just fighting for a better world. You're living in this one. Don't forget to actually live.
              </p>
            </div>

            <div className="bg-white border border-purple-200 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">6. Remember Your Why</h4>
              <p className="text-gray-700 mb-3">
                Why do you care? Who are you fighting for? What world do you want to create?
              </p>
              <p className="text-gray-700 text-sm">
                Write it down. Look at it when you're struggling. Let it anchor you when everything feels pointless.
              </p>
            </div>

            <div className="bg-white border border-purple-200 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">7. Allow Yourself to Grieve</h4>
              <p className="text-gray-700 mb-3">
                Hope doesn't mean you can't be sad. Hope doesn't mean you can't be angry. Hope doesn't mean you can't grieve.
              </p>
              <p className="text-gray-700 text-sm italic">
                Feel your feelings. Cry. Rage. Mourn. Then get back up and keep fighting.
              </p>
            </div>

            <div className="bg-white border border-purple-200 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">8. Look for the Helpers</h4>
              <p className="text-gray-700 mb-3">
                In every tragedy, there are people helping. In every crisis, there are people organizing. In every dark moment, there are people lighting candles.
              </p>
              <p className="text-gray-700 text-sm">
                Find them. Join them. Be one of them.
              </p>
            </div>
          </div>

          {/* Section 6 */}
          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            A Letter to My Exhausted Sisters
          </h2>

          <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-8 rounded-xl mb-8">
            <p className="text-gray-800 leading-relaxed mb-4">
              Dear sister,
            </p>

            <p className="text-gray-800 leading-relaxed mb-4">
              I know you're tired. I know you're scared. I know you're angry. I know you want to give up.
            </p>

            <p className="text-gray-800 leading-relaxed mb-4">
              I know because I feel it too. Every single day.
            </p>

            <p className="text-gray-800 leading-relaxed mb-4">
              But here's what I also know: You are stronger than you think. You are more powerful than they want you to believe. You are part of a movement that spans centuries and continents.
            </p>

            <p className="text-gray-800 leading-relaxed mb-4">
              You are not alone in this fight. Millions of women around the world are fighting beside you. Women you'll never meet. Women who speak different languages. Women who live in different countries. Women who face different struggles.
            </p>

            <p className="text-gray-800 leading-relaxed mb-4">
              But we're all fighting for the same thing: A world where women are free. A world where women are safe. A world where women are equal. A world where women are human.
            </p>

            <p className="text-gray-800 leading-relaxed mb-4">
              We may not see that world in our lifetimes. But every action we take brings it closer. Every woman we help. Every girl we empower. Every man we educate. Every system we challenge. Every boundary we set. Every time we refuse to be silent.
            </p>

            <p className="text-gray-800 leading-relaxed mb-4">
              <strong>It all matters.</strong>
            </p>

            <p className="text-gray-800 leading-relaxed mb-4">
              So rest when you need to. Cry when you need to. Rage when you need to. But don't give up.
            </p>

            <p className="text-gray-800 leading-relaxed mb-4">
              Because the world needs you. The women who come after us need you. The girls being born today need you.
            </p>

            <p className="text-gray-800 leading-relaxed mb-4">
              And I need you.
            </p>

            <p className="text-gray-800 leading-relaxed mb-4">
              We're in this together. When you can't carry the hope, I'll carry it for you. When I can't carry it, you'll carry it for me. And together, we'll keep the flame alive.
            </p>

            <p className="text-gray-800 leading-relaxed mb-4">
              <strong>With solidarity and love,</strong>
            </p>

            <p className="text-gray-800 leading-relaxed">
              A sister in the struggle
            </p>
          </div>

          {/* Conclusion */}
          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            The Choice We Make Every Day
          </h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Hope is not something you have or don't have. It's something you choose. Every single day.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Some days, choosing hope is easy. The sun is shining. You read good news. You feel strong. You feel powerful. You feel like you can change the world.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Other days, choosing hope is the hardest thing you'll ever do. The news is devastating. You're exhausted. You're scared. You feel powerless. You feel like nothing will ever change.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Choose hope anyway.</strong>
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Not because you're certain we'll win. But because the alternative—despair, giving up, letting them win without a fight—is unacceptable.
          </p>

          <p className="text-gray-700 leading-relaxed mb-8">
            Choose hope because you refuse to let the darkness have the final word. Choose hope because you believe in a better world, even if you never see it. Choose hope because you owe it to the women who fought before you and the women who will fight after you.
          </p>

          <div className="bg-purple-600 text-white p-8 rounded-xl mb-8">
            <p className="text-2xl font-bold mb-4">
              Choose hope because you are powerful.
            </p>
            <p className="text-lg mb-4">
              Choose hope because you matter.
            </p>
            <p className="text-lg mb-4">
              Choose hope because what you do matters.
            </p>
            <p className="text-lg mb-0">
              Choose hope because together, we are unstoppable.
            </p>
          </div>

          {/* Final Message */}
          <div className="border-t-2 border-purple-200 pt-8 mt-12">
            <p className="text-xl text-gray-800 font-semibold mb-4">
              The world is heavy. The fight is hard. The road is long.
            </p>
            <p className="text-xl text-gray-800 font-semibold mb-4">
              But we're still here. We're still fighting. We're still hoping.
            </p>
            <p className="text-xl text-gray-800 font-semibold mb-4">
              And that, in itself, is revolutionary.
            </p>
            <p className="text-2xl text-purple-600 font-bold mt-8">
              Keep going, sister. The world needs your hope. ✊💜
            </p>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 text-white">
          <h3 className="text-2xl font-bold mb-4">Turn Your Hope Into Action</h3>
          <p className="text-lg mb-6 opacity-90">
            Hope without action is just a wish. Join our movement and be part of the change.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/volunteer"
              className="bg-white text-purple-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Volunteer With Us
            </Link>
            <Link
              to="/donate"
              className="bg-purple-800 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-900 transition-colors whitespace-nowrap"
            >
              Support Our Work
            </Link>
            <Link
              to="/community"
              className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-purple-600 transition-colors whitespace-nowrap"
            >
              Join Our Community
            </Link>
          </div>
        </div>

        {/* Share */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <p className="text-gray-600 mb-4">Share this article:</p>
          <div className="flex gap-4">
            <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap">
              <i className="ri-facebook-fill mr-2"></i>
              Facebook
            </button>
            <button className="bg-sky-500 text-white px-6 py-2 rounded-lg hover:bg-sky-600 transition-colors whitespace-nowrap">
              <i className="ri-twitter-fill mr-2"></i>
              Twitter
            </button>
            <button className="bg-pink-600 text-white px-6 py-2 rounded-lg hover:bg-pink-700 transition-colors whitespace-nowrap">
              <i className="ri-instagram-fill mr-2"></i>
              Instagram
            </button>
          </div>
        </div>
      </article>
    </div>
  );
}
